import React from "react";
import styled from "styled-components";
import {
  NotionBlockHealthStatus,
  NotionBlockMetadataJson,
  NotionDatabasePropertyMetadataJson,
} from "../types";

interface TemplateHealthStatusProps {
  blockIssues: Array<NotionBlockMetadataJson> | null;
  propertyIssues: Array<NotionDatabasePropertyMetadataJson> | null;
  healthCheckLoading: boolean;
}

const HEALTH_STATUS_MESSAGE: Record<NotionBlockHealthStatus, string> = {
  deleted: "has been deleted",
  healthy: "is healthy",
  not_found: "cannot be found",
  unexpected_id: "contains an unexpected ID",
  unexpected_type: "has been assigned an incorrect type",
  unknown: "has no health information available",
  unhealthy_properties: "has one or more issues with it's properties",
};

const TemplateStatus = styled.p`
  :focus {
    outline: none;
  }
`;
const TemplateStatusSummary = styled.summary`
  &:focus {
    outline: none;
  }
`;
const BlockIssue = styled.p``;
const PropertyIssue = styled.p``;

export default function TemplateHealthStatus({
  blockIssues,
  propertyIssues,
  healthCheckLoading,
}: TemplateHealthStatusProps) {
  if (blockIssues === null || propertyIssues === null) {
    return <p>Template health information not available.</p>;
  }

  const numIssues: number =
    (blockIssues?.length || 0) + (propertyIssues?.length || 0);

  const healthLabel: string = healthCheckLoading
    ? "loading..."
    : numIssues === 0
    ? "healthy"
    : `${numIssues} issues detected`;

  const healthIcon: React.ReactNode = healthCheckLoading ? (
    <></>
  ) : numIssues === 0 ? (
    <i className="ri-shield-check-fill pr-1 text-success" />
  ) : (
    <i className="ri-error-warning-fill pr-1 text-danger" />
  );

  const issueRows = blockIssues.map((issue) => {
    let propertyIssues: Array<React.ReactNode> = [];
    if (issue.type === "database") {
      propertyIssues = Object.values(issue.properties)
        .filter(hasIssue)
        .map((propertyIssue) => (
          <PropertyIssue
            key={propertyIssue.id}
            className="pl-4 text-sm mt-0 mb-1"
          >
            <i className="ri-subtract-line pr-1 text-danger" />
            {propertyIssue.type} property{" "}
            <span className="tag font-semibold">{propertyIssue.name}</span>{" "}
            {HEALTH_STATUS_MESSAGE[propertyIssue.healthStatus || "unknown"]}.
          </PropertyIssue>
        ));
    }

    return (
      <div key={issue.id}>
        <BlockIssue key={issue.id} className="pl-2 mt-0 mb-1 text-sm">
          <i className="ri-file-reduce-fill pr-1 text-danger" />
          <span className="tag font-semibold">{issue.title}</span> {issue.type}{" "}
          {HEALTH_STATUS_MESSAGE[issue.healthStatus || "unknown"]}.
        </BlockIssue>
        {propertyIssues}
      </div>
    );
  });

  return (
    <details className="accordion p-0" open>
      <TemplateStatusSummary key="summary" className="accordion__summary m-0">
        <TemplateStatus
          className={`u-flex u-items-center text-md font-semibold my-1 ${
            healthCheckLoading ? "animated loading loading-left" : ""
          }`}
        >
          {healthIcon} Current status: {healthLabel}
        </TemplateStatus>
      </TemplateStatusSummary>
      {!healthCheckLoading && issueRows}
    </details>
  );
}

export function hasIssue(blockMetadata: {
  healthStatus: NotionBlockHealthStatus | null;
}): boolean {
  return !["healthy", "unknown"].includes(
    blockMetadata.healthStatus || "unknown"
  );
}
